import { useCallback } from 'react';
import { useStore } from '../store';
import { api } from '../api';
import type { Transaction } from '@tangtung/shared';

export function useTransactions() {
    const dbUser = useStore((s) => s.dbUser);
    const transactions = useStore((s) => s.transactions);
    const addTransaction = useStore((s) => s.addTransaction);
    const showToast = useStore((s) => s.showToast);
    const calcResult = useStore((s) => s.calcResult);
    const clearCalc = useStore((s) => s.clearCalc);

    const saveTransaction = useCallback(
        async (type: 'cash' | 'qr', amount?: number): Promise<{ id: number } | null> => {
            const finalAmount = amount ?? calcResult;

            if (finalAmount <= 0) {
                showToast('กรุณาใส่จำนวนเงิน');
                return null;
            }

            // Offline mode
            if (!dbUser) {
                const tx: Transaction = {
                    id: Date.now(),
                    user_id: 0,
                    amount: finalAmount,
                    payment_type: type,
                    note: null,
                    slip_url: null,
                    created_at: new Date().toISOString(),
                };
                addTransaction(tx);
                clearCalc();
                showToast('บันทึกสำเร็จ!');
                return { id: tx.id };
            }

            // Online mode
            try {
                const result = await api.createTransaction({
                    line_user_id: dbUser.line_user_id,
                    amount: finalAmount,
                    payment_type: type,
                });

                if (result.data) {
                    addTransaction(result.data);
                    clearCalc();
                    showToast('บันทึกสำเร็จ!');
                    return { id: result.data.id };
                }
            } catch (error) {
                console.error('Save transaction error:', error);
                showToast('เกิดข้อผิดพลาด');
            }

            return null;
        },
        [dbUser, calcResult, addTransaction, clearCalc, showToast]
    );

    const getTodayStats = useCallback(() => {
        // Get today's date in Bangkok timezone (YYYY-MM-DD format)
        const now = new Date();
        const todayStr = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

        const todayTx = transactions.filter((tx) => {
            // Parse UTC timestamp from API (format: "2026-02-04 18:54:08")
            // Add 'Z' to indicate it's UTC, then convert to Bangkok date
            const utcDateStr = tx.created_at.replace(' ', 'T') + 'Z';
            const txDate = new Date(utcDateStr);
            const txDateStr = txDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
            return txDateStr === todayStr;
        });

        const cashTx = todayTx.filter((tx) => tx.payment_type === 'cash');
        const qrTx = todayTx.filter((tx) => tx.payment_type === 'qr');

        return {
            total: todayTx.reduce((sum, tx) => sum + tx.amount, 0),
            count: todayTx.length,
            cash: cashTx.reduce((sum, tx) => sum + tx.amount, 0),
            qr: qrTx.reduce((sum, tx) => sum + tx.amount, 0),
            transactions: todayTx,
        };
    }, [transactions]);

    const refreshTransactions = useCallback(async () => {
        if (!dbUser) return;
        try {
            const result = await api.getTransactions(dbUser.line_user_id, 100);
            if (result.data) {
                // Update store
                useStore.getState().setTransactions(result.data);
            }
        } catch (error) {
            console.error('Refresh transactions error:', error);
        }
    }, [dbUser]);

    return { transactions, saveTransaction, getTodayStats, refreshTransactions };
}
